// お客様の流れ：実在のお客様（読み込んだ督促ケース・本番の下書き）や空から作った会話を、時点（検証ケース）ごとに追うための組み立て。
// 起点のケースから「続きのケース」（parentCaseId）を辿り、枝分かれは元の時点の下に字下げして並べる
import { INDIVIDUAL_CASE_SET, isArchived } from "./types"
import type { EvaluationCase, ScenarioMessage } from "./types"

/** 流れの中の1時点。depth は起点からの深さ（枝分かれの字下げ）、number は時点の番号（1始まり・深さ + 1） */
export type FlowPoint = { record: EvaluationCase; depth: number; number: number }

/** 1つの流れ（起点のケースと、そこからつながる続きのケース）。points は起点から深さ優先の順 */
export type Flow = { root: EvaluationCase; points: FlowPoint[] }

/**
 * お客様ごとの流れ。督促ケースから読み込んでいない流れ（本番の下書きから取り込んだ・空から作った）は sourceDunningCaseId が null で、
 * 1つの流れを1人のお客様として括り、label（見出し）を持つ
 */
export type FlowCustomer = {
  key: string
  sourceDunningCaseId: number | null
  sourceUserId: number | null
  /** 取り込み元の督促ケースが無い流れの見出し（起点のケース名から取り込みの印を除いたもの） */
  label: string | null
  flows: Flow[]
}

/** ケース名の先頭の取り込みの印（「[実データ取込] 」「【本番】」など）を除く */
export const stripTitleTag = (title: string) =>
  title.replace(/^(\s*(\[[^\]]*\]|【[^】]*】)\s*)+/, "").trim() || title

/** お客様の流れに出すケース：個別の箱、または流れ・取り込み元を持つケース（しまったケースは出さない） */
export const isFlowCase = (record: EvaluationCase) =>
  !isArchived(record) &&
  (record.caseSet === INDIVIDUAL_CASE_SET || !!record.parentCaseId || !!record.sourceDunningCaseId)

const byAsOf = (a: EvaluationCase, b: EvaluationCase) =>
  Date.parse(a.scenario.asOf) - Date.parse(b.scenario.asOf) || a.id - b.id

/** ケースを流れにまとめる。元のケースが一覧に無い（しまった等）続きのケースは、それ自体を起点にする */
export function buildFlows(cases: EvaluationCase[]): Flow[] {
  const members = cases.filter(isFlowCase)
  const ids = new Set(members.map((record) => record.id))
  const children = new Map<number, EvaluationCase[]>()
  const roots: EvaluationCase[] = []
  for (const record of members) {
    const parentId = record.parentCaseId
    if (parentId && ids.has(parentId)) {
      children.set(parentId, [...(children.get(parentId) ?? []), record])
    } else {
      roots.push(record)
    }
  }
  const walk = (record: EvaluationCase, depth: number, seen: Set<number>): FlowPoint[] => {
    if (seen.has(record.id)) return []
    seen.add(record.id)
    const next = [...(children.get(record.id) ?? [])].sort(byAsOf)
    return [
      { record, depth, number: depth + 1 },
      ...next.flatMap((child) => walk(child, depth + 1, seen)),
    ]
  }
  const visited = new Set<number>()
  const flows = roots.sort(byAsOf).map((root) => ({ root, points: walk(root, 0, visited) }))
  // 元のケースをたどると輪になって起点に着かないケースも、消さずに起点にして出す
  for (const record of [...members].sort(byAsOf)) {
    if (!visited.has(record.id)) flows.push({ root: record, points: walk(record, 0, visited) })
  }
  return flows
}

/**
 * 流れをお客様ごとにまとめる。取り込み元の督促ケースがある流れはその督促ケースごと、無い流れ（本番の下書きから取り込んだ・
 * 空から作った）は1つの流れを1人のお客様として、それぞれ別に括る（督促ケースのあるお客様を先に並べる）
 */
export function groupByCustomer(flows: Flow[]): FlowCustomer[] {
  const customers = new Map<string, FlowCustomer>()
  for (const flow of flows) {
    const caseId = flow.root.sourceDunningCaseId ?? null
    const key = caseId ? `case-${caseId}` : `flow-${flow.root.id}`
    const customer = customers.get(key) ?? {
      key,
      sourceDunningCaseId: caseId,
      sourceUserId: flow.root.sourceUserId ?? null,
      label: caseId ? null : stripTitleTag(flow.root.title),
      flows: [],
    }
    customer.flows.push(flow)
    customers.set(key, customer)
  }
  return [...customers.values()].sort(
    (a, b) => Number(!a.sourceDunningCaseId) - Number(!b.sourceDunningCaseId)
  )
}

/** ケースを含む流れ */
export const flowOf = (flows: Flow[], caseId: number | null | undefined) =>
  caseId ? flows.find((flow) => flow.points.some((point) => point.record.id === caseId)) : undefined

/** その時点（基準日時）までの最後のお客様の発言 */
export const lastCustomerMessage = (record: EvaluationCase): ScenarioMessage | undefined => {
  const asOf = Date.parse(record.scenario.asOf)
  return [...record.scenario.conversation]
    .filter((message) => message.direction === "inbound" && Date.parse(message.at) <= asOf)
    .sort((a, b) => Date.parse(a.at) - Date.parse(b.at))
    .at(-1)
}

/** 抜粋（改行をつなげ、長ければ末尾を…にする） */
export const excerpt = (text: string, length: number) => {
  const flat = text.replace(/\s+/g, " ").trim()
  return flat.length > length ? `${flat.slice(0, length)}…` : flat
}

/** 時点の見出しに使う日時（例「9/19 12:10」） */
export const pointTimeLabel = (iso: string) =>
  new Date(iso).toLocaleString("ja-JP", {
    timeZone: "Asia/Tokyo",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })

/** 督促ケース詳細の URL（取り込み元のお客様へのリンク） */
export const dunningCaseHref = (dunningCaseId: number) => `/admin/dunning-cases/${dunningCaseId}`
